import { createApp } from 'vue';
import ContentVueEditor from '@components/ContentVueEditor.vue';
import { translations } from '@tools/translations/translations';
import { translationMixin } from '@elements/translation';
import { REGEX_HTTPS_URL } from '@tools/regex';
import { showSubmitLoader } from '@atoms/spinner';

/**
 * Init create offer step2 Vue
 */
export const createOfferDescription = (container) => {
  const component = {
    delimiters: [ '${', '}' ],
    mixins: [ translationMixin ],
    components: { ContentVueEditor },
    data() {
      return {
        description: '',
        descriptionMin: 200,
        descriptionMax: 1500,
        helpVisible: false,
        trad: {
          info: '',
          infos: '',
        },
        discount: {
          value: '',
          fieldDisplayed: false,
          fieldHasNotBeenModified: true,
          max: 200,
          info: '',
        },
        remote: {
          value: '',
          fieldDisplayed: false,
          fieldHasNotBeenModified: true,
          max: 200,
          info: '',
        },
        errorMessage: '',
        virtualVisitLink: '',
        isVirtualVisitLinkValid: true,
      };
    },
    computed: {
      descriptionLength() {
        return this.description.replace(/<[^>]*>|\r?\n/g, '').trim().length;
      },
    },
    watch: {
      description() {
        this.toggleFieldClass('.rich-text', true);
        this.toggleFieldClass('.editor-counter', true);
      },
      discount: {
        handler(discount) {
          this.discount.value = discount.value;
          this.changeTrad();
          if (this.discount.fieldDisplayed) {
            this.$nextTick(() => {
              this.updateDiscountStatus();
            });
          }
        },
        deep: true,
      },
      remote: {
        handler(remote) {
          this.remote.value = remote.value;
          this.changeTrad();
          if (this.remote.fieldDisplayed) {
            this.$nextTick(() => {
              this.updateRemoteStatus();
            });
          }
        },
        deep: true,
      },
    },
    mounted() {
      this.initDisplay();
      this.errorMessage = translations.validators['form.validation.error.description.charnumber'];
      this.trad.info = translations.messages['form.offer.char.info'];
      this.trad.infos = translations.messages['form.offer.char.infos'];
    },
    beforeMount() {
      this.initDiscount();
      this.initRemote();
      this.initVirtualVisitLink();
      this.initDescription();
    },
    methods: {
      /**
       * changeTrad
       * @description Select singular or plural trad depending on description length.
       */
      changeTrad() {
        // Empty editor contains a single line break, therefore we decrease the length by one
        this.discount.info = this.transchoice('form.offer.char.infos', { count: this.discount.value.length });
        this.remote.info = this.transchoice('form.offer.char.infos', { count: this.remote.value.length });
      },
      /**
       * validateForm
       * @description Allow submit only if description length is valid
       */
      validateForm() {
        const form = document.querySelector(`${container} form`);
        const submitButtons = form.querySelectorAll('.offer-creation-cta [type=submit]');
        let alreadyClicked = false;
        let targetSubmit = null;

        submitButtons.forEach((submit) => {
          submit.addEventListener('click', (event) => {
            targetSubmit = event.currentTarget;
            if (this.discount.fieldDisplayed) {
              this.discount.fieldHasNotBeenModified = false;
            }

            if (this.remote.fieldDisplayed) {
              this.remote.fieldHasNotBeenModified = false;
            }

            if (submit.value) {
              const actionField = document.createElement('input');
              actionField.type = 'hidden';
              actionField.name = `setup[${submit.value}]`;
              actionField.value = submit.value;
              form.appendChild(actionField);
            }

            if (targetSubmit.id === 'setup_prev') {
              document.querySelector('textarea[required="required"]').removeAttribute('required');
              return true;
            }

            this.isVirtualVisitLinkValid = (this.virtualVisitLink !== '') ? new RegExp(REGEX_HTTPS_URL).test(this.virtualVisitLink) : true;

            if (alreadyClicked || !this.isVirtualVisitLinkValid) {
              return false;
            }

            if (!this.isDescriptionValid() || !this.isDiscountValid() || !this.isRemoteValid()) {
              this.scrollToFieldError();
              event.preventDefault();

              return false;
            }

            return true;
          });
        });

        form.addEventListener('submit', () => {
          if (!alreadyClicked) {
            alreadyClicked = true;
          }
          showSubmitLoader(targetSubmit);
        });
        form.setAttribute('data-ready', true);
      },
      scrollToFieldError() {
        let element = null;

        if (!this.isDiscountValid()) {
          element = document.getElementById('setup_data_discount');
        } else if (!this.isRemoteValid()) {
          element = document.getElementById('setup_data_remote_offer');
        } else if (!this.isDescriptionValid()) {
          [ element ] = document.getElementsByName('setup[data][description]');
          this.updateDescriptionStatus();
        }

        if (!element) {
          return;
        }

        element.scrollIntoView(
          { behavior: 'instant', block: 'center' },
        );
      },
      onDiscountInput() {
        this.discount.fieldHasNotBeenModified = false;
      },
      onRemoteInput() {
        this.remote.fieldHasNotBeenModified = false;
      },
      initDiscount() {
        this.discount.value = window.initialValues.discount || '';
      },
      initRemote() {
        this.remote.value = window.initialValues.remote || '';
      },
      initVirtualVisitLink() {
        this.virtualVisitLink = window.initialValues.virtual_visit_link || '';
      },
      initDescription() {
        this.description = window.initialValues.description || '';
      },
      isDescriptionValid() {
        // Empty editor contains a single line break, therefore we decrease the length by one
        return this.descriptionLength >= this.descriptionMin && this.descriptionLength <= this.descriptionMax;
      },
      isDiscountValid() {
        if (this.discount.fieldHasNotBeenModified) {
          return true;
        }

        return (!this.discount.fieldDisplayed || this.discount.value.length > 0) && this.discount.value.length <= this.discount.max;
      },
      isRemoteValid() {
        if (this.remote.fieldHasNotBeenModified) {
          return true;
        }

        return (!this.remote.fieldDisplayed || this.remote.value.length > 0) && this.remote.value.length <= this.remote.max;
      },
      updateDescriptionStatus() {
        this.toggleFieldClass('.rich-text', this.isDescriptionValid());
        this.toggleFieldClass('.editor-counter', this.isDescriptionValid());
      },
      updateDiscountStatus() {
        const isDiscountValid = this.isDiscountValid();

        this.toggleFieldClass('.create-offer--step-description-discount .input-text', isDiscountValid);
      },
      updateRemoteStatus() {
        const isRemoteValid = this.isRemoteValid();

        this.toggleFieldClass('.create-offer--step-description-remote .input-text', isRemoteValid);
      },
      toggleFieldClass(element, isValid) {
        const elementText = document.querySelector(element);

        if (isValid) {
          elementText.classList.add('valid');
          elementText.classList.remove('error');
        } else {
          elementText.classList.add('error');
          elementText.classList.remove('valid');
        }
      },
      /**
       * initDisplay
       * @description disable submit next button
       */
      initDisplay() {
        if (this.discount.value) {
          this.discount.fieldDisplayed = true;
        }
        if (this.remote.value) {
          this.remote.fieldDisplayed = true;
        }
        this.validateForm();
      },
      addDiscount(event) {
        event.preventDefault();
        this.discount.fieldDisplayed = true;
        this.$nextTick(() => {
          this.updateDiscountStatus();
        });
      },
      removeDiscount(event) {
        event.preventDefault();
        this.discount.fieldDisplayed = false;
      },
      addRemote(event) {
        event.preventDefault();
        this.remote.fieldDisplayed = true;
        this.$nextTick(() => {
          this.updateRemoteStatus();
        });
      },
      removeRemote(event) {
        event.preventDefault();
        this.remote.fieldDisplayed = false;
      },
    },
  };

  return createApp(component).mount(container);
};
